import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

/**
 * RMDV brand mark — a "V" carrying four verification tickboxes.
 *
 * Geometry is the canonical mark (see docs/design/specimen/rmdv-mark.svg):
 *   - wider V so the larger boxes keep equal whitespace to the strokes
 *   - each column's boxes sit on a line parallel to its arm => equal box→V gap
 *     for the top and bottom rows by construction
 *
 * Colours default to the theme so the mark stays in sync with the brand:
 *   - V stroke   -> currentColor (callers set `color`, defaults to text.primary
 *                   so it adapts to light/dark)
 *   - box fill   -> primary.main (the pinned brand blue, #1F6FEB)
 *   - tick       -> white (reads on the blue box in both colour modes)
 */
const VPATH = 'M2 8 L60 96 L118 8';
const BOXES = [
  [31.4, 20],
  [88.6, 20],
  [47.2, 44],
  [72.8, 44],
];
const HALF = 9;
const RX = 4;

const tickPath = (cx, cy) =>
  `M${(cx - 5).toFixed(2)} ${cy} L${(cx - 1.6).toFixed(2)} ${(cy + 3.3).toFixed(2)} L${(cx + 5).toFixed(2)} ${(cy - 4.2).toFixed(2)}`;

export function LogoMark({ size = 28, boxColor = 'primary.main', tickColor = '#fff', title = 'RMDV', sx, ...props }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 120 120"
      width={size}
      height={size}
      role="img"
      aria-label={title}
      sx={{ display: 'block', flexShrink: 0, color: 'text.primary', ...sx }}
      {...props}
    >
      <title>{title}</title>
      {/* V — inherits currentColor (text.primary) so it adapts to the theme */}
      <path
        d={VPATH}
        fill="none"
        stroke="currentColor"
        strokeWidth={7.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Box component="g" strokeLinecap="round" strokeLinejoin="round">
        {BOXES.map(([cx, cy]) => (
          <React.Fragment key={`${cx}-${cy}`}>
            <Box
              component="rect"
              x={cx - HALF}
              y={cy - HALF}
              width={HALF * 2}
              height={HALF * 2}
              rx={RX}
              sx={{ fill: boxColor }}
            />
            <path d={tickPath(cx, cy)} fill="none" stroke={tickColor} strokeWidth={3} />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

/**
 * RMDV lockup — the mark plus the "RMDV" wordmark (blue DV).
 * Pass showWord={false} for an icon-only lockup (e.g. collapsed sidebar).
 */
export function LogoLockup({ markSize = 30, fontSize = 20, showWord = true, sx, ...props }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }} {...props}>
      <LogoMark size={markSize} />
      {showWord && (
        <Typography
          component="span"
          sx={{
            fontWeight: 800,
            fontSize,
            letterSpacing: '-0.02em',
            lineHeight: 1,
            userSelect: 'none',
            color: 'text.primary',
          }}
        >
          RM
          <Box component="span" sx={{ color: 'primary.main' }}>
            DV
          </Box>
        </Typography>
      )}
    </Box>
  );
}

export default LogoLockup;
